import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, resolveComponent as _resolveComponent, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "export-history-modal__section" }
const _hoisted_2 = { class: "export-history-modal__select export-history-modal__select--timeframe" }
const _hoisted_3 = { class: "export-history-modal__select" }
const _hoisted_4 = { class: "export-history-modal__select-title" }
const _hoisted_5 = { class: "export-history-modal__section" }
const _hoisted_6 = { class: "export-history-modal__base-quote" }
const _hoisted_7 = { class: "export-history-modal__select-title export-history-modal__base-quote-title--base" }
const _hoisted_8 = /*#__PURE__*/_createElementVNode("div", { class: "export-history-modal__base-quote-separator" }, "/", -1)
const _hoisted_9 = { class: "export-history-modal__select-title export-history-modal__base-quote-title--quote" }
const _hoisted_10 = { class: "export-history-modal__section" }
const _hoisted_11 = { class: "export-history-modal__select" }
const _hoisted_12 = { class: "export-history-modal__select-title" }

import { isEmpty, orderBy } from 'lodash/fp';
import { computed, reactive, type PropType, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import HistoryRangePicker from '@exchange/libs/components/history/HistoryRangePicker.vue';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { usePromiseThrowing } from '@exchange/libs/composables/shared/src/lib/usePromise';
import { type GenerateData } from '@exchange/libs/exports/service/src';
import { modalVariant, type ModalVariant } from '@exchange/libs/modals/src';
import { toastManagerInstance, SimpleToast } from '@exchange/libs/toasts/src';
import type { Subaccount } from '@exchange/libs/trading-accounts/service/src';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import type { CurrencyModel } from '@exchange/libs/utils/currency/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

interface ExportHistoryModalState {
  selectedTimeframe: {
    from?: string;
    to?: string;
  };
  selectedSubaccounts: Array<Subaccount>;
  selectedExportTrades: boolean;
  selectedExportTransactions: boolean;
  selectedBase: Array<CurrencyModel>;
  selectedQuote: Array<CurrencyModel>;
  selectedCurrencies: Array<CurrencyModel>;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ExportHistoryModal',
  props: {
  currencies: { type: Object as PropType<Array<CurrencyModel>>, required: true },
  generate: { type: Function as PropType<(data: GenerateData) => Promise<void>>, required: true },
  markets: { type: Object as PropType<Array<string>>, required: true },
  subaccounts: { type: Object as PropType<Array<Subaccount>>, required: true },
  variant: { type: String as PropType<ModalVariant>, default: modalVariant.dark },
},
  setup(__props) {

const props = __props;

const { t, te } = useI18n({ useScope: 'global' });

const { isXS } = useMQBreakpoints();
const currencyOptions = computed(() => orderBy(['id'], ['asc'], props.currencies));
const subaccountsOptions = computed(() => props.subaccounts);

const state = reactive<ExportHistoryModalState>({
  selectedTimeframe: {
    from: undefined,
    to: undefined,
  },
  selectedSubaccounts: [...subaccountsOptions.value],
  selectedExportTrades: false,
  selectedExportTransactions: false,
  selectedBase: [...currencyOptions.value],
  selectedQuote: [...currencyOptions.value],
  selectedCurrencies: [...currencyOptions.value],
});

const generateExportDisabled = computed(() => {
  const tradesAreNotOk = state.selectedExportTrades && (state.selectedBase.length === 0 || state.selectedQuote.length === 0);
  const transactionsAreNotOk = state.selectedExportTransactions && state.selectedCurrencies.length === 0;

  return (!state.selectedExportTrades && !state.selectedExportTransactions) || state.selectedSubaccounts.length === 0 || tradesAreNotOk || transactionsAreNotOk;
});

const generateMarketsFormBaseQuote = () => {
  const base = state.selectedBase;
  const quote = state.selectedQuote;
  const currenciesNumber = currencyOptions.value.length;
  const pairs = base.flatMap((b) => quote.map((q) => `${b.id}_${q.id}`));

  if ((base.length === currenciesNumber && quote.length === currenciesNumber) || pairs.length >= CONSTANTS.EXPORT_TRADES_MAX_PAIR_NUMBER) {
    return [];
  }

  return pairs;
};

const { loading: generating, createPromise: generateExport } = usePromiseThrowing(async () => {
  const accounts = state.selectedSubaccounts.map((a) => a.accountId);

  const trades = {
    requested: state.selectedExportTrades,
    markets: generateMarketsFormBaseQuote(),
    accounts,
  };

  const transactions = {
    requested: state.selectedExportTransactions,
    currencies: state.selectedCurrencies.map((c) => c.id),
    accounts,
  };

  await props
    .generate({
      trades,
      transactions,
      ...(!isEmpty(state.selectedTimeframe.from) && { from: state.selectedTimeframe.from }),
      ...(!isEmpty(state.selectedTimeframe.to) && { to: state.selectedTimeframe.to }),
    })
    .catch((e) => {
      logger.error('Generation export failed', e);

      const error = e?.data?.error;
      const lKey = `modules.accountHistory.export.requests.errors.${error}`;
      const message = te(lKey) ? t(lKey) : t('fundamentals.error.text');

      toastManagerInstance.addToast({
        content: SimpleToast,
        props: {
          variant: 'failed',
          title: 'modules.apiKeys.delete.failed.title',
          translatedMessage: message,
        },
      });
    });
});

watch(currencyOptions, () => {
  state.selectedCurrencies = [...currencyOptions.value];
  state.selectedBase = [...currencyOptions.value];
  state.selectedQuote = [...currencyOptions.value];
});
watch(subaccountsOptions, () => {
  // @ts-ignore bignumber types...
  state.selectedSubaccounts = [...subaccountsOptions.value];
});

return (_ctx: any,_cache: any) => {
  const _component_x_select_multi = _resolveComponent("x-select-multi")!
  const _component_ot_checkbox = _resolveComponent("ot-checkbox")!
  const _component_x_button = _resolveComponent("x-button")!
  const _component_x_modal = _resolveComponent("x-modal")!

  return (_openBlock(), _createBlock(_component_x_modal, {
    class: "export-history-modal",
    type: "xs-fullscreen",
    name: "export-history",
    variant: __props.variant,
    title: _ctx.$t('modules.accountHistory.export.modal.title')
  }, {
    content: _withCtx(() => [
      _createElementVNode("section", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(HistoryRangePicker, {
            value: state.selectedTimeframe,
            "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((state.selectedTimeframe) = $event)),
            position: "bottom-center",
            "on-modal": true,
            "months-to-show": 2,
            variant: __props.variant === 'dark' ? 'dark' : 'light'
          }, null, 8, ["value", "variant"])
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.$t('modules.accountHistory.export.modal.dropdowns.selectAccount.title')), 1),
          _createVNode(_component_x_select_multi, {
            value: state.selectedSubaccounts,
            "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((state.selectedSubaccounts) = $event)),
            label: "name",
            "dropdown-max-height": "330px",
            variant: __props.variant === 'dark' ? 'dark-outlined' : 'light-outlined',
            multiple: true,
            "menu-top-space": !_unref(isXS),
            options: subaccountsOptions.value
          }, null, 8, ["value", "variant", "menu-top-space", "options"])
        ])
      ]),
      _createElementVNode("section", _hoisted_5, [
        _createVNode(_component_ot_checkbox, {
          selected: state.selectedExportTrades,
          "onUpdate:selected": _cache[2] || (_cache[2] = ($event: any) => ((state.selectedExportTrades) = $event)),
          class: "export-history-modal__checkbox",
          "color-variant": __props.variant,
          label: _ctx.$t('modules.accountHistory.export.modal.includeTrades.label')
        }, null, 8, ["selected", "color-variant", "label"]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, _toDisplayString(_ctx.$t('modules.accountHistory.export.modal.dropdowns.base.title')), 1),
          _createVNode(_component_x_select_multi, {
            value: state.selectedBase,
            "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((state.selectedBase) = $event)),
            class: "export-history-modal__base-quote-value--base",
            label: "id",
            "dropdown-max-height": "220px",
            variant: __props.variant === 'dark' ? 'dark-outlined' : 'light-outlined',
            multiple: true,
            "menu-top-space": !_unref(isXS),
            options: currencyOptions.value,
            disabled: !state.selectedExportTrades
          }, null, 8, ["value", "variant", "menu-top-space", "options", "disabled"]),
          _hoisted_8,
          _createElementVNode("div", _hoisted_9, _toDisplayString(_ctx.$t('modules.accountHistory.export.modal.dropdowns.quote.title')), 1),
          _createVNode(_component_x_select_multi, {
            value: state.selectedQuote,
            "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((state.selectedQuote) = $event)),
            class: "export-history-modal__base-quote-value--quote",
            label: "id",
            "dropdown-max-height": "260px",
            variant: __props.variant === 'dark' ? 'dark-outlined' : 'light-outlined',
            multiple: true,
            "menu-top-space": !_unref(isXS),
            options: currencyOptions.value,
            disabled: !state.selectedExportTrades
          }, null, 8, ["value", "variant", "menu-top-space", "options", "disabled"])
        ])
      ]),
      _createElementVNode("section", _hoisted_10, [
        _createVNode(_component_ot_checkbox, {
          selected: state.selectedExportTransactions,
          "onUpdate:selected": _cache[5] || (_cache[5] = ($event: any) => ((state.selectedExportTransactions) = $event)),
          class: "export-history-modal__checkbox",
          "color-variant": __props.variant,
          label: _ctx.$t('modules.accountHistory.export.modal.includeTransactions.label')
        }, null, 8, ["selected", "color-variant", "label"]),
        _createElementVNode("div", _hoisted_11, [
          _createElementVNode("div", _hoisted_12, _toDisplayString(_ctx.$t('modules.accountHistory.export.modal.dropdowns.selectCurrencies.title')), 1),
          _createVNode(_component_x_select_multi, {
            value: state.selectedCurrencies,
            "onUpdate:value": _cache[6] || (_cache[6] = ($event: any) => ((state.selectedCurrencies) = $event)),
            label: "id",
            "dropdown-max-height": "260px",
            variant: __props.variant === 'dark' ? 'dark-outlined' : 'light-outlined',
            multiple: true,
            "menu-top-space": !_unref(isXS),
            options: currencyOptions.value,
            disabled: !state.selectedExportTransactions
          }, null, 8, ["value", "variant", "menu-top-space", "options", "disabled"])
        ])
      ])
    ]),
    buttons: _withCtx(() => [
      _createVNode(_component_x_button, {
        variant: "primary",
        block: true,
        loading: _unref(generating),
        disabled: generateExportDisabled.value,
        onClick: _unref(generateExport)
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('modules.accountHistory.export.modal.actions.generate')), 1)
        ]),
        _: 1
      }, 8, ["loading", "disabled", "onClick"])
    ]),
    _: 1
  }, 8, ["variant", "title"]))
}
}

})